import { Button, DatePicker, DropDown, Table, TextField } from "@/components";
import { ButtonVariant } from "@/components/Button/types";
import { DropDownOption } from "@/components/DropDown/DropDown";
import useCustomTranslation from "@/localization/useCustomTranslation";
import { useUserProfile } from "@/hooks/query/useUserProfile";
import theme from "@/theme";
import { ProjectByCompany } from "@/types/branches.type";
import { ILogFilters, LogAction, LogStatus } from "@/types/log.type";
import { Branch, GetListResponse } from "@/types/portalCompany.type";
import { UserRoles } from "@/types/user.type";
import { Field, Form, Formik } from "formik";
import React, { FC, useMemo } from "react";
import { shallowEqual } from "react-redux";
import { ValidationSchema } from "./schema";
import * as Styled from "./style";

interface IProps {
  onSubmit: (values: ILogFilters) => void;
  filters: ILogFilters;
  companies: GetListResponse[];
  branches: ProjectByCompany[];
}

export type IFilterValues = ILogFilters;

const statuses = Object.values(LogStatus);
const listOfActions = Object.values(LogAction);

const initialValues: ILogFilters = {
  email: "",
  company_id: "",
  action: undefined,
  status: undefined,
  branch_id: "",
  date: ""
};

const getFilterControls = (
  dirty: boolean,
  resetForm: () => void,
  values: ILogFilters,
  setValues: (values: ILogFilters) => void,
  submitForm: () => void,
  prefixedT: (prefix: string) => string
) => {
  const isChangedFilters = !shallowEqual(initialValues, values);
  const onRestoreFilters = () => {
    setValues(initialValues);
    submitForm();
  };

  return (
    <Styled.Controls>
      {(dirty || isChangedFilters) && (
        <Button
          onClick={!isChangedFilters ? resetForm : onRestoreFilters}
          variant={ButtonVariant.outline}
          width="120px"
          height="34px"
          color={theme.colors.coral}
          testId="change-log-discard"
        >
          {prefixedT(`FILTERS.${!dirty ? "ERASE" : "CLEAR"}`)}
        </Button>
      )}
      <Button
        disabled={!dirty}
        type="submit"
        variant={ButtonVariant.outline}
        width="120px"
        height="34px"
        testId="change-log-filter"
      >
        {prefixedT("FILTERS.SUBMIT")}
      </Button>
    </Styled.Controls>
  );
};

const Filters: FC<IProps> = ({ onSubmit, filters, companies, branches }) => {
  const { data: profile } = useUserProfile();

  const { prefixedT, t } = useCustomTranslation("CHANGE_LOG");

  const isSuperAdmin = useMemo(
    () => profile?.role?.role_code === UserRoles.SUPER_ADMIN,
    [profile]
  );

  return (
    <Styled.Container>
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={ValidationSchema}
        onSubmit={onSubmit}
        initialValues={{ ...initialValues, ...filters }}
        enableReinitialize
      >
        {({
          errors,
          setFieldValue,
          values,
          dirty,
          resetForm,
          setValues,
          submitForm
        }) => {
          const branchesOptions: Branch[] = isSuperAdmin
            ? (() => {
                const selectedCompany = companies.find(
                  (el: GetListResponse) =>
                    Number(el.id) === Number(values.company_id)
                );
                return selectedCompany?.branches || [];
              })()
            : (branches?.map((branch) => ({
                id: branch.id,
                name: branch.branch_name,
                active: branch.active
              })) ?? []);
          return (
            <Form>
              <Table.Header
                title={prefixedT("FILTERS.TITLE")}
                controls={getFilterControls(
                  dirty,
                  resetForm,
                  values,
                  setValues,
                  submitForm,
                  prefixedT
                )}
              />
              <Styled.Card>
                <Styled.CardWrapper>
                  <Field
                    as={TextField}
                    name="email"
                    label={prefixedT("FILTERS.EMAIL.LABEL")}
                    placeholder={prefixedT("FILTERS.EMAIL.PLACEHOLDER")}
                    error={errors.email && t(errors.email)}
                    styles={{ label: Styled.Label }}
                    testId="change-log-email"
                  />
                  <Field
                    name="action"
                    as={DropDown}
                    label={prefixedT("FILTERS.ACTION.LABEL")}
                    options={listOfActions.map((el: LogAction) => ({
                      value: el,
                      label: prefixedT(`ACTIONS.${el}`)
                    }))}
                    placeholder={prefixedT("FILTERS.ACTION.PLACEHOLDER")}
                    onChange={(option: DropDownOption) =>
                      setFieldValue("action", option.value)
                    }
                    styles={{ label: Styled.Label }}
                    testId="change-log-action"
                  />
                  <Field
                    name="status"
                    as={DropDown}
                    label={prefixedT("FILTERS.STATUS.LABEL")}
                    options={statuses.map((el: LogStatus) => ({
                      value: el,
                      label: prefixedT(`STATUSES.${el}`)
                    }))}
                    placeholder={prefixedT("FILTERS.STATUS.PLACEHOLDER")}
                    onChange={(option: DropDownOption) =>
                      setFieldValue("status", option.value)
                    }
                    styles={{ label: Styled.Label }}
                    testId="change-log-status"
                  />
                  {profile?.role?.role_code === UserRoles.SUPER_ADMIN && (
                    <Field
                      name="company_id"
                      as={DropDown}
                      label={prefixedT("FILTERS.COMPANY.LABEL")}
                      options={companies.map((el) => ({
                        value: el.id,
                        label: el.name
                      }))}
                      placeholder={prefixedT("FILTERS.COMPANY.PLACEHOLDER")}
                      onChange={(option: DropDownOption) =>
                        setFieldValue("company_id", option.value)
                      }
                      styles={{ label: Styled.Label }}
                      testId="change-log-company"
                    />
                  )}
                  <Field
                    name="branch_id"
                    as={DropDown}
                    label={prefixedT("FILTERS.BRANCH.LABEL")}
                    disabled={isSuperAdmin && !values.company_id}
                    options={branchesOptions.map((el) => ({
                      value: el.id,
                      label: el.name
                    }))}
                    placeholder={prefixedT("FILTERS.BRANCH.PLACEHOLDER")}
                    onChange={(option: DropDownOption) =>
                      setFieldValue("branch_id", option.value)
                    }
                    styles={{ label: Styled.Label }}
                    testId="change-log-branch"
                  />
                  <Field
                    as={DatePicker}
                    name="date"
                    label={prefixedT("FILTERS.DATE.LABEL")}
                    placeholder={prefixedT("FILTERS.DATE.PLACEHOLDER")}
                    onChange={(date: Date | null) =>
                      setFieldValue("date", date)
                    }
                    disableFuture
                    styles={{ label: Styled.Label }}
                    testId="change-log-date"
                  />
                </Styled.CardWrapper>
              </Styled.Card>
            </Form>
          );
        }}
      </Formik>
    </Styled.Container>
  );
};

export default Filters;
